import type { SjbEmploymentKey, SjbJobsFacets, SjbSalonRow } from "./_props";
import { fmt } from "@/lib/_format";

export const EMPLOYMENT_LABELS: Record<SjbEmploymentKey, string> = {
  full_time: "正社員",
  freelance: "業務委託",
  part_time: "パート",
  contract: "契約",
};

export const EMPLOYMENT_ORDER: SjbEmploymentKey[] = ["full_time", "freelance", "part_time", "contract"];

export const SALARY_CATEGORY_LABELS: Record<string, string> = {
  monthly: "月給",
  hourly: "時給",
  yearly: "年俸",
  daily: "日給",
  commission: "完全歩合",
};

export type SortKey = "video_first" | "salary_desc" | "salary_asc" | "newest";

export const SORT_LABELS: Record<SortKey, string> = {
  video_first: "動画あり優先",
  salary_desc: "給与が高い順",
  salary_asc: "給与が低い順",
  newest: "新着順",
};

export const FILTER_CONTROL_CLASS = "w-full h-10 text-sm border border-slate-300 rounded px-2 bg-white";
export const FILTER_CHIP_ROW_CLASS = "min-h-10 flex flex-wrap items-center gap-1.5";

export interface FilterState {
  area: string;
  smallArea: string;
  salaryMin: number | null;
  employments: Set<SjbEmploymentKey>;
  socialInsurance: boolean;
  trainingForBeginners: boolean;
  query: string;
  sort: SortKey;
}

export const INITIAL_FILTERS: FilterState = {
  area: "",
  smallArea: "",
  salaryMin: null,
  employments: new Set(),
  socialInsurance: false,
  trainingForBeginners: false,
  query: "",
  sort: "video_first",
};

export function countActiveFilters(f: FilterState): number {
  let n = 0;
  if (f.area) n++;
  if (f.smallArea) n++;
  if (f.salaryMin) n++;
  n += f.employments.size;
  if (f.socialInsurance) n++;
  if (f.trainingForBeginners) n++;
  if (f.query) n++;
  return n;
}

export function filterAndSort(salons: SjbSalonRow[], f: FilterState): SjbSalonRow[] {
  const arr = salons.filter((s) => {
    if (f.area && s.middle_area !== f.area) return false;
    if (f.smallArea && s.small_area !== f.smallArea) return false;
    if (f.salaryMin) {
      const max = s.salary_max_overall ?? s.salary_min_overall ?? 0;
      if (max < f.salaryMin) return false;
    }
    if (f.employments.size > 0) {
      const hit = s.employment_categories.some((e) => f.employments.has(e));
      if (!hit) return false;
    }
    if (f.socialInsurance && !s.has_social_insurance) return false;
    if (f.trainingForBeginners && !s.has_training_for_beginners) return false;
    if (f.query) {
      const q = f.query.toLowerCase();
      const hay = `${s.salon_name || ""} ${s.middle_area || ""} ${s.small_area || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  arr.sort((a, b) => sortCompare(a, b, f.sort));
  return arr;
}

export function sortCompare(a: SjbSalonRow, b: SjbSalonRow, sort: SortKey): number {
  if (sort === "video_first") {
    if (a.videos_count !== b.videos_count) return b.videos_count - a.videos_count;
    return (b.latest_updated_at || "").localeCompare(a.latest_updated_at || "");
  }
  if (sort === "salary_desc") {
    return (b.salary_max_overall || b.salary_min_overall || 0) - (a.salary_max_overall || a.salary_min_overall || 0);
  }
  if (sort === "salary_asc") {
    return (a.salary_min_overall || a.salary_max_overall || Infinity) - (b.salary_min_overall || b.salary_max_overall || Infinity);
  }
  return (b.latest_updated_at || "").localeCompare(a.latest_updated_at || "");
}

export function FilterDock({
  filters,
  setFilters,
  facets,
  onReset,
  activeCount,
  open,
  setOpen,
  resultCount,
}: {
  filters: FilterState;
  setFilters: (f: FilterState) => void;
  facets: SjbJobsFacets;
  onReset: () => void;
  activeCount: number;
  open: boolean;
  setOpen: (b: boolean) => void;
  resultCount: number;
}) {
  const employmentOptions = EMPLOYMENT_ORDER
    .map((k) => ({ key: k, count: facets.employment_categories.find((e) => e.key === k)?.count ?? 0 }))
    .filter((e) => e.count > 0);
  const smallAreaOptions = facets.areas.find((a) => a.key === filters.area)?.small_areas ?? [];

  return (
    <>
      {open && (
        <button
          type="button"
          aria-label="閉じる"
          onClick={() => setOpen(false)}
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
        />
      )}
      <div
        className={`fixed inset-x-0 bottom-0 z-50 transition-transform duration-300 ${
          open ? "translate-y-0" : "translate-y-[calc(100%-56px)]"
        }`}
      >
        <div className="bg-white shadow-[0_-8px_32px_rgba(0,0,0,0.12)] border-t border-slate-200 rounded-t-2xl max-w-[1200px] mx-auto">
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="w-full px-4 py-3 flex items-center justify-between min-h-[56px] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007aff]/40 focus-visible:ring-inset"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-slate-900">絞り込み</span>
              {activeCount > 0 && (
                <span className="text-[10px] font-bold bg-[#007aff] text-white px-1.5 py-0.5 rounded">
                  {activeCount}
                </span>
              )}
              {!open && (
                <span className="text-[11px] text-slate-500 ml-2">
                  {fmt(resultCount)} 件のサロン
                </span>
              )}
            </div>
            <span className="text-xs text-slate-500">{open ? "閉じる ▼" : "開く ▲"}</span>
          </button>

          {open && (
            <div className="px-4 pb-4 max-h-[70vh] overflow-y-auto">
              <div className="flex items-center justify-end mb-3">
                {activeCount > 0 && (
                  <button
                    type="button"
                    onClick={onReset}
                    className="text-[11px] font-semibold text-slate-500 hover:text-[#007aff] underline"
                  >
                    リセット ({activeCount})
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 items-start">
                <FilterSection title="フリーワード" className="lg:col-span-3">
                  <input
                    type="text"
                    value={filters.query}
                    onChange={(e) => setFilters({ ...filters, query: e.target.value })}
                    placeholder="サロン名 / エリア"
                    className={FILTER_CONTROL_CLASS}
                  />
                </FilterSection>

                <FilterSection title="都道府県" className="lg:col-span-3">
                  <select
                    value={filters.area}
                    onChange={(e) => setFilters({ ...filters, area: e.target.value, smallArea: "" })}
                    className={FILTER_CONTROL_CLASS}
                  >
                    <option value="">すべて</option>
                    {facets.areas.map((a) => (
                      <option key={a.key} value={a.key}>{a.key} ({a.count})</option>
                    ))}
                  </select>
                </FilterSection>

                <FilterSection title="市区町村" className="lg:col-span-3">
                  <select
                    value={filters.smallArea}
                    onChange={(e) => setFilters({ ...filters, smallArea: e.target.value })}
                    disabled={!filters.area}
                    className={`${FILTER_CONTROL_CLASS} disabled:bg-slate-100 disabled:text-slate-400`}
                  >
                    <option value="">{filters.area ? "すべて" : "都道府県を選択"}</option>
                    {smallAreaOptions.map((a) => (
                      <option key={a.key} value={a.key}>{a.key} ({a.count})</option>
                    ))}
                  </select>
                </FilterSection>

                <FilterSection title="給与（下限）" className="lg:col-span-3">
                  <select
                    value={filters.salaryMin ?? ""}
                    onChange={(e) => setFilters({ ...filters, salaryMin: e.target.value ? Number(e.target.value) : null })}
                    className={FILTER_CONTROL_CLASS}
                  >
                    <option value="">指定なし</option>
                    <option value="200000">月給 20万円以上</option>
                    <option value="250000">月給 25万円以上</option>
                    <option value="300000">月給 30万円以上</option>
                    <option value="400000">月給 40万円以上</option>
                    <option value="500000">月給 50万円以上</option>
                  </select>
                </FilterSection>

                <FilterSection title="雇用形態" className="lg:col-span-6">
                  <div className={FILTER_CHIP_ROW_CLASS}>
                    {employmentOptions.map((e) => (
                      <ChipToggle
                        key={e.key}
                        label={EMPLOYMENT_LABELS[e.key]}
                        count={e.count}
                        active={filters.employments.has(e.key)}
                        onToggle={() => {
                          const next = new Set(filters.employments);
                          if (next.has(e.key)) next.delete(e.key);
                          else next.add(e.key);
                          setFilters({ ...filters, employments: next });
                        }}
                      />
                    ))}
                  </div>
                </FilterSection>

                <FilterSection title="こだわり" className="lg:col-span-6">
                  <div className={FILTER_CHIP_ROW_CLASS}>
                    <ChipToggle
                      label="社保完備"
                      active={filters.socialInsurance}
                      onToggle={() => setFilters({ ...filters, socialInsurance: !filters.socialInsurance })}
                    />
                    <ChipToggle
                      label="未経験OK"
                      active={filters.trainingForBeginners}
                      onToggle={() => setFilters({ ...filters, trainingForBeginners: !filters.trainingForBeginners })}
                    />
                  </div>
                </FilterSection>
              </div>

              <div className="sticky bottom-0 -mx-4 px-4 pt-3 mt-4 bg-white border-t border-slate-100 flex gap-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="flex-1 text-sm font-bold py-3 rounded-lg bg-[#007aff] text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007aff]/40 focus-visible:ring-offset-2"
                >
                  {fmt(resultCount)} サロンを見る
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export function FilterSection({ title, children, className = "" }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <div className="h-4 text-[11px] font-bold uppercase tracking-wider mb-1.5 text-slate-500 leading-4">{title}</div>
      {children}
    </div>
  );
}

export function ChipToggle({
  label,
  count,
  active,
  onToggle,
}: {
  label: string;
  count?: number;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`text-[11px] font-semibold px-2.5 py-1.5 rounded-full border transition-colors ${
        active
          ? "bg-[#007aff] text-white border-[#007aff]"
          : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
      }`}
    >
      {label}
      {count != null && <span className={`ml-1 ${active ? "opacity-80" : "text-slate-400"}`}>{count}</span>}
    </button>
  );
}
